import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import myaxios from "@/lib/axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiLogIn } from "react-icons/fi";

// ─── SSO(448) begin — remove with the SSO feature ───
const SSO_ERRORS: Record<string, string> = {
  no_access:
    "You signed in successfully, but no RaidoHub access is set up for your account yet. Please contact your administrator.",
  access_denied: "Sign-in was cancelled.",
  invalid_token: "We could not verify the response from your identity provider.",
  invalid_state: "That sign-in link is no longer valid. Please try again.",
  expired: "The sign-in attempt timed out. Please try again.",
  unknown_connection: "That sign-in method is not available.",
  provider_unavailable:
    "Your identity provider could not be reached. Please try again shortly.",
  provider_error: "Your identity provider reported a problem. Please try again.",
  default: "Single sign-on failed. Please try again.",
};
// ─── SSO(448) end ───

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<undefined | string>(
    undefined
  );

  useEffect(() => {
    document.title = "RaidoHub | Login";
  }, []);

  // ─── SSO(448) begin — remove with the SSO feature ───
  // A failed round trip comes back here as a code in the query string; the
  // detail behind it is in the server's audit log, not the browser.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("sso_error");
    if (code) {
      setErrorMessage(SSO_ERRORS[code] ?? SSO_ERRORS.default);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  // ─── SSO(448) end ───

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(undefined);

    // Track login attempt with Heap
    window.heap?.track("Login Attempt", { username });

    try {
      const response = await myaxios.post("/api/auth/login", {
        username,
        password,
      });

      if (response.data.success && response.data.token) {
        // Set the auth token cookie with proper attributes
        document.cookie = `auth_token=${response.data.token}; path=/; max-age=86400; secure; sameSite=strict`;

        // Small delay to ensure cookie is set
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if cookie was set
        const cookies = document.cookie.split(";");
        const authCookie = cookies.find((c) =>
          c.trim().startsWith("auth_token=")
        );

        if (authCookie) {
          // Track successful login with Heap
          window.heap?.track("Login Success", { username });

          // Identify the user in Heap
          window.heap?.identify(username);

          // Redirect to home page
          window.location.href = "/";
        } else {
          setErrorMessage(
            "Failed to set authentication cookie. Please try again."
          );

          // Track cookie error with Heap
          window.heap?.track("Login Error", {
            type: "cookie_error",
            message: "Failed to set authentication cookie",
          });
        }
      } else if (response.data.message) {
        setErrorMessage(response.data.message);

        // Track login rejection with Heap
        window.heap?.track("Login Error", {
          type: "rejected",
          message: response.data.message,
        });
      } else {
        setErrorMessage("Something went wrong. Please try again later.");

        // Track generic login error with Heap
        window.heap?.track("Login Error", {
          type: "unknown",
          message: "Something went wrong",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again later.";
      setErrorMessage(errorMessage);

      // Track login exception with Heap
      window.heap?.track("Login Error", {
        type: "exception",
        message: errorMessage,
      });
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="flex flex-col items-center justify-center mb-10 z-10">
        <h1 className="text-3xl font-semibold text-white tracking-tight mb-1">
          Welcome to <span className="text-blue-400">RaidoHub</span>
        </h1>
        <div className="h-1 w-12 bg-blue-500 rounded mt-2"></div>
      </div>

      <Card className="w-full max-w-md border-0 shadow-xl bg-gray-800/70 backdrop-blur-sm z-10">
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>
            <div>Please enter your username and password to login.</div>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage && (
            <div className="opacity-70 dark:bg-orange-900 border dark:border-orange-600 rounded px-4 py-2 mb-4 text-sm">
              {errorMessage}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              placeholder=""
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="border-gray-500 bg-gray-700/50 hover:border-gray-400 
              focus:border-blue-400/30 focus:outline-none focus:ring-0 
              transition-all duration-300 focus:bg-gray-700/80 
              focus:shadow-[0_0_0_1.5px_rgba(96,165,250,0.8),0_4px_10px_rgba(37,99,235,0.35)]
              motion-safe:focus:animate-pulse-subtle"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border-gray-500 bg-gray-700/50 hover:border-gray-400 
              focus:border-blue-400/30 focus:outline-none focus:ring-0 
              transition-all duration-300 focus:bg-gray-700/80 
              focus:shadow-[0_0_0_1.5px_rgba(96,165,250,0.8),0_4px_10px_rgba(37,99,235,0.35)]
              motion-safe:focus:animate-pulse-subtle"
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            onClick={handleLogin}
          >
            <div className="flex items-center justify-center gap-2 text-white">
              <FiLogIn className="text-lg" />
              <span>Sign in</span>
            </div>
          </Button>

          {/* ─── SSO(448) begin — remove with the SSO feature ─── */}
          <Link
            to="/login/sso"
            className="text-xs text-gray-400 underline underline-offset-2 hover:text-gray-200"
          >
            Log in with SSO
          </Link>
          {/* ─── SSO(448) end ─── */}
        </CardFooter>
      </Card>

      <div className="mt-6 text-center text-gray-300 z-10">
        <p className="mb-1">Can't login? We've got you covered.</p>
        <Link
          to={"/auth/forgot"}
          className="text-blue-400 hover:text-blue-300 hover:underline"
        >
          Reset your password
        </Link>
      </div>
    </div>
  );
}

export default LoginPage;
